import React, { useState } from 'react';
import { ShieldOff, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 4 — Block rider confirmation modal.
 * Triggered from HistoryTab (low-rating row) or after a trip.
 * Hits POST /api/driver/v2/{driverId}/blocked-riders.
 */
const BlockRiderModal = ({ driverId, rider, onClose, onBlocked }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!rider) return null;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/driver/v2/${driverId}/blocked-riders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rider_id: rider.id || rider.rider_id,
          rider_name: rider.name || rider.rider_name || null,
          reason: reason.trim() || null,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Block failed');
      }
      onBlocked?.();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/65 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="block-rider-modal"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
            <ShieldOff className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Block this passenger?</h3>
            <p className="text-xs text-gray-500">{rider.name || rider.rider_name || 'Anonymous rider'}</p>
          </div>
        </div>

        <p className="text-[13px] text-gray-600 mb-3">
          They will <span className="font-semibold">never</span> be matched with you again. You can manage blocks any time from Account → Safety.
        </p>

        <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Reason (optional)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={240}
          rows={2}
          placeholder="e.g. Aggressive behavior, cancelled at pickup..."
          data-testid="block-rider-reason"
          className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none text-sm resize-none"
        />

        {error && (
          <p className="mt-2 text-[12px] text-rose-700 bg-rose-50 rounded-lg p-2 inline-flex items-center gap-1.5" data-testid="block-rider-error">
            <AlertTriangle className="w-3.5 h-3.5" /> {error}
          </p>
        )}

        <div className="flex gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            data-testid="block-rider-cancel"
            className="flex-1 h-12 rounded-full bg-gray-100 text-gray-700 font-semibold text-sm hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            data-testid="block-rider-confirm"
            className="flex-1 h-12 rounded-full bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 text-white font-semibold text-sm"
          >
            {submitting ? 'Blocking…' : 'Yes, block'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlockRiderModal;
